/**
 * Builds the daily logbook as an A4-landscape PDF (for Share / Download).
 * jspdf + jspdf-autotable are dynamically imported so they stay out of the main bundle.
 */
type Row = Record<string, unknown>;
interface Field { key: string; label: string; unit: string }
interface Section { label: string; fields: Field[] }

export interface DailyLogPdfInput {
  org?: { name?: string; subtitle?: string };
  logoDataUrl?: string;
  title: string;
  equipName: string;
  equipLoc: string;
  equipmentId: string;
  date: string;
  sections: Section[];
  rows: Row[];
  cell: (r: Row, f: Field) => string;
}

const NAME = 'U. N. Mehta Institute of Cardiology & Research Centre';
const SUB = 'Affiliated to B. J. Medical College, Ahmedabad-380016, INDIA';

const dmy = (d: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : d;
};

export async function loadImageDataUrl(url: string): Promise<string | undefined> {
  try {
    const res = await fetch(url);
    if (!res.ok) return undefined;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(typeof fr.result === 'string' ? fr.result : undefined);
      fr.onerror = () => resolve(undefined);
      fr.readAsDataURL(blob);
    });
  } catch {
    return undefined;
  }
}

export async function buildDailyLogPdf(input: DailyLogPdfInput): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 10;

  const org = input.org ?? {};

  if (input.logoDataUrl) {
    try { doc.addImage(input.logoDataUrl, 'PNG', M, M, 22, 14); } catch { /* bad image */ }
  }
  doc.setTextColor(18, 58, 134);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(15);
  doc.text(org.name ?? NAME, pageW / 2, M + 6, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(`(${org.subtitle ?? SUB})`, pageW / 2, M + 11.5, { align: 'center' });
  doc.setDrawColor(18, 58, 134); doc.setLineWidth(0.5);
  doc.line(M, M + 15, pageW - M, M + 15);

  doc.setTextColor(17, 17, 17);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
  doc.text(`Daily Logbook - ${input.title}`, pageW / 2, M + 22, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(`${input.equipName}${input.equipLoc ? ' | ' + input.equipLoc : ''}`, pageW / 2, M + 27, { align: 'center' });
  doc.text(`Date: ${dmy(input.date)}      Equipment ID: ${input.equipmentId}      Readings: ${input.rows.length}`, pageW / 2, M + 32, { align: 'center' });

  const fields = input.sections.flatMap((s) => s.fields);
  const remarksCol = fields.length + 2; // Time(0) + fields + Operator + Remarks

  const head = [
    [
      { content: 'Time', rowSpan: 2 },
      ...input.sections.map((s) => ({ content: s.label, colSpan: s.fields.length })),
      { content: 'Operator', rowSpan: 2 },
      { content: 'Remarks', rowSpan: 2 },
    ],
    fields.map((f) => ({ content: f.unit ? `${f.label} (${f.unit})` : f.label })),
  ];
  const body = input.rows.map((r) => [
    String(r['Time'] ?? ''),
    ...fields.map((f) => input.cell(r, f)),
    String(r['Operator Name'] ?? ''),
    String(r['Observation / Remarks'] ?? ''),
  ]);

  autoTable(doc, {
    head: head as never,
    body,
    startY: M + 37,
    margin: { left: M, right: M, bottom: 20 },
    tableLineColor: [0, 0, 0],
    tableLineWidth: 0.2,
    styles: { fontSize: 7, cellPadding: 1.1, lineColor: [0, 0, 0], lineWidth: 0.15, textColor: [17, 17, 17], halign: 'center', valign: 'middle' },
    headStyles: { fillColor: [223, 230, 242], textColor: [17, 17, 17], fontStyle: 'bold', lineWidth: 0.2 },
    columnStyles: { [remarksCol]: { halign: 'left', cellWidth: 34 } },
    didDrawPage: () => {
      // sign block: bottom-LEFT of every page
      doc.setDrawColor(51, 51, 51); doc.setLineWidth(0.3);
      doc.line(M, pageH - 15, M + 55, pageH - 15);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(34, 34, 34);
      doc.text('Electrical Engineer', M + 27.5, pageH - 11, { align: 'center' });
    },
  });

  return doc.output('blob');
}
